import {
  extendVariants,
  Select as NextUISelect,
  SelectItem as NextUISelectItem,
} from "@heroui/react";
import clsx from "clsx";
import { WithId } from "core/types";
import { ReactNode } from "react";

const ExtendedSelect = extendVariants(NextUISelect, {
  variants: {
    variant: {
      inline: {
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        label:
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-normal group-data-[has-value=true]:font-medium",
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
        listbox: "[&>*]:gap-1", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
      },
      outline: {
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        label: [
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-medium",
        ],
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
        listbox: "[&>*]:gap-1", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
      },
      minimal: {
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        innerWrapper: ["!pt-0"],
        label:
          "group-data-[has-value=true]:!hidden group-data-[hover=true]:text-gray-900",
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
        ],
        listbox: "[&>*]:gap-1", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
      },
      horizontal: {
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
          "w-auto min-w-[80px]",
        ],
        innerWrapper: ["!pt-0"],
        label:
          "min-w-max font-medium group-data-[hover=true]:text-gray-900 !text-sm",
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal group-data-[hover=true]:text-gray-900",
        ],
        listbox: "[&>*]:gap-1 min-w-full w-max",
      },
    },
    color: {
      default: {},
      primary: {},
      secondary: {},
      success: {},
      warning: {},
      danger: {},
    },
    size: {
      sm: {
        label: "text-tiny",
        trigger: "h-8 min-h-8 px-2 rounded-small",
        value: "text-small",
      },
      md: {
        trigger: "h-10 min-h-10 rounded-medium",
        value: "text-small",
      },
      lg: {
        trigger: "h-12 min-h-12 rounded-large",
        value: "text-medium",
      },
    },
    radius: {
      none: {
        trigger: "rounded-none",
      },
      sm: {
        trigger: "rounded-small",
      },
      md: {
        trigger: "rounded-medium",
      },
      lg: {
        trigger: "rounded-large",
      },
      full: {
        trigger: "rounded-full",
      },
    },
    labelPlacement: {
      outside: {
        base: "flex flex-col",
        label: "text-sm",
        value: "font-normal text-sm",
      },
      "outside-left": {
        base: "flex-row items-center flex-nowrap items-start",
        label: "relative pe-2 text-foreground flex items-center",
      },
      inside: {
        label: "text-sm cursor-pointer",
        trigger: "flex-col items-start justify-center gap-0",
      },
    },
    fullWidth: {
      true: {
        base: "w-full",
      },
      false: {
        base: "min-w-40",
      },
    },
    isDisabled: {
      true: {
        base: "opacity-100 pointer-events-none",
        trigger: "pointer-events-none bg-gray-200",
      },
    },
    isInvalid: {
      true: {
        trigger: "border-danger",
        label: "text-danger",
        value: "text-danger",
        selectorIcon: "text-danger",
        errorMessage: "text-danger-dark",
      },
    },
    isRequired: {
      true: {
        label: "after:content-['*'] after:text-danger after:ms-0.5",
      },
    },
    isMultiline: {
      true: {
        label: "relative min-h-fit",
        trigger: "!h-auto",
      },
      false: {
        value: "truncate",
      },
    },
    disableAnimation: {
      true: {
        trigger: "after:transition-none",
        base: "transition-none",
        label: "transition-none",
        selectorIcon: "transition-none",
      },
      false: {
        base: "transition-background motion-reduce:transition-none !duration-150",
        label: [
          "will-change-auto",
          "origin-top-left",
          // Using RTL here as Tailwind CSS doesn't support `start` and `end` logical properties for transforms yet.
          "rtl:origin-top-right",
          "!duration-200",
          "!ease-out",
          "transition-[transform,color,left,opacity]",
          "motion-reduce:transition-none",
        ],
        selectorIcon:
          "transition-transform duration-150 ease motion-reduce:transition-none",
      },
    },
    disableSelectorIconRotation: {
      true: {},
      false: {
        selectorIcon: "data-[open=true]:rotate-180",
      },
    },
  },
  defaultVariants: {
    variant: "inline",
    color: "primary",
    size: "md",
    labelPlacement: "inside",
    fullWidth: true,
    isDisabled: false,
    isMultiline: false,
    disableSelectorIconRotation: false,
  },
  compoundVariants: [
    // [inline, outline] & color
    {
      variant: ["inline", "outline"],
      color: "primary",
      class: {
        // @ts-ignore
        trigger: [
          "data-[open=true]:ring-primary",
          "data-[focus=true]:ring-primary",
        ],
      },
    },
    // labelPlacement=outside & default
    {
      labelPlacement: "outside",
      color: "default",
      class: {
        // @ts-ignore
        label: "group-data-[filled=true]:text-gray-900",
      },
    },
    // isDisabled & variant
    {
      isDisabled: true,
      variant: ["minimal", "horizontal"],
      class: {
        // @ts-ignore
        trigger: ["bg-white !text-default-disabled"],
        label: "!text-default-disabled",
        value: "!text-default-disabled",
      },
    },
    // isInvalid & variant
    {
      isInvalid: true,
      variant: ["inline", "outline"],
      class: {
        // @ts-ignore
        trigger: "!ring-danger group-data-[focus=true]:ring-danger",
      },
    },
    {
      isInvalid: true,
      variant: ["minimal", "horizontal"],
      class: {
        // @ts-ignore
        trigger: "bg-white",
      },
    },
    // isInvalid & variant & size
    {
      isInvalid: true,
      size: "md",
      variant: ["minimal", "horizontal"],
      class: {
        // @ts-ignore
        errorMessage: "pl-2",
      },
    },
    {
      isInvalid: true,
      size: "lg",
      variant: ["minimal", "horizontal"],
      class: {
        // @ts-ignore
        errorMessage: "pl-2.5",
      },
    },
    // size & labelPlacement
    {
      labelPlacement: "inside",
      size: "sm",
      class: {
        // @ts-ignore
        trigger: "h-8 min-h-8 py-1.5 px-3",
      },
    },
    {
      labelPlacement: "inside",
      size: "md",
      class: {
        // @ts-ignore
        trigger: "h-10 min-h-10 py-2",
      },
    },
    {
      labelPlacement: "inside",
      size: "lg",
      class: {
        // @ts-ignore
        label: "text-sm",
        trigger: "h-12 min-h-12 py-2.5 gap-0",
      },
    },
    {
      labelPlacement: "outside",
      isMultiline: false,
      class: {
        // @ts-ignore
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
    },
    {
      labelPlacement: "outside",
      isMultiline: false,
      class: {
        // @ts-ignore
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
    },
    // labelPlacement=[inside]
    {
      labelPlacement: ["inside"],
      class: {
        // @ts-ignore
        label:
          "group-data-[filled=true]:scale-100 group-data-[filled=true]:text-[10px]",
      },
    },
    // inside & size
    {
      labelPlacement: "inside",
      size: ["sm", "md"],
      class: {
        // @ts-ignore
        label: "text-sm",
      },
    },
    {
      labelPlacement: "inside",
      isMultiline: false,
      size: "sm",
      class: {
        // @ts-ignore
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_theme(fontSize.sm)/2_-_8px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-4",
      },
    },
    {
      labelPlacement: "inside",
      isMultiline: false,
      size: "md",
      class: {
        // @ts-ignore
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-4",
      },
    },
    {
      labelPlacement: "inside",
      isMultiline: false,
      size: "lg",
      class: {
        // @ts-ignore
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+__10px/2_-_8px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-5",
      },
    },
    // inside & size & inline
    {
      labelPlacement: "inside",
      variant: "inline",
      isMultiline: false,
      size: "sm",
      class: {
        // @ts-ignore
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_8px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-3",
      },
    },
    {
      labelPlacement: "inside",
      variant: "inline",
      isMultiline: false,
      size: "md",
      class: {
        // @ts-ignore
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-3",
      },
    },
    {
      labelPlacement: "inside",
      variant: "inline",
      isMultiline: false,
      size: "lg",
      class: {
        // @ts-ignore
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
        innerWrapper: "group-data-[has-label=true]:pt-3",
      },
    },
    // inside & size & minimal
    {
      labelPlacement: "inside",
      variant: "minimal",
      isMultiline: false,
      size: "sm",
      class: {
        // @ts-ignore
        label: ["!text-sm !translate-y-0"],
        innerWrapper: "group-data-[has-label=true]:pt-2",
      },
    },
    {
      labelPlacement: "inside",
      variant: "minimal",
      isMultiline: false,
      size: "md",
      class: {
        // @ts-ignore
        label: ["!text-sm !translate-y-0"],
        innerWrapper: "group-data-[has-label=true]:pt-2",
      },
    },
    {
      labelPlacement: "inside",
      variant: "minimal",
      isMultiline: false,
      size: "lg",
      class: {
        // @ts-ignore
        label: ["!text-sm !translate-y-0"],
        innerWrapper: "group-data-[has-label=true]:pt-2",
      },
    },
    // outside & size
    {
      labelPlacement: "outside",
      size: "sm",
      isMultiline: false,
      class: {
        // @ts-ignore
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_16px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_8px)]",
      },
    },
    {
      labelPlacement: "outside",
      isMultiline: false,
      size: "md",
      class: {
        // @ts-ignore
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_20px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_10px)]",
      },
    },
    {
      labelPlacement: "outside",
      isMultiline: false,
      size: "lg",
      class: {
        // @ts-ignore
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_24px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_12px)]",
      },
    },
    // outside-left & size
    {
      labelPlacement: "outside-left",
      size: "sm",
      isMultiline: false,
      class: {
        // @ts-ignore
        label: ["h-8 flex items-center"],
      },
    },
    {
      labelPlacement: "outside-left",
      isMultiline: false,
      size: "md",
      class: {
        // @ts-ignore
        label: ["h-10 flex items-center"],
      },
    },
    {
      labelPlacement: "outside-left",
      isMultiline: false,
      size: "lg",
      class: {
        // @ts-ignore
        label: ["h-12 flex items-center"],
      },
    },
    // isMultiline & labelPlacement="outside"
    {
      labelPlacement: "outside",
      isMultiline: true,
      class: {
        // @ts-ignore
        label: "pb-1.5",
      },
    },
    // text truncate labelPlacement=[inside,outside]
    {
      labelPlacement: ["inside", "outside"],
      class: {
        // @ts-ignore
        label: ["pe-2", "max-w-full", "text-ellipsis", "overflow-hidden"],
      },
    },
  ],
});

type ExtendedSelectProps = React.ComponentProps<typeof ExtendedSelect>;

type Variant = Extract<
  ExtendedSelectProps["variant"],
  "inline" | "outline" | "minimal" | "horizontal"
>;

export type SelectProps<T extends WithId = WithId> = Omit<
  ExtendedSelectProps,
  "variant" | "color" | "radius" | "labelPlacement" | "children"
> & {
  items: T[];
  renderItem: (item: T) => ReactNode;
  variant?: Variant;
};

export function Select<T extends WithId = WithId>({
  renderItem,
  ...props
}: SelectProps<T>) {
  return (
    <ExtendedSelect
      {...props}
      color="primary"
      labelPlacement={
        props.variant === "inline" || props.variant === "minimal"
          ? "inside"
          : props.variant === "outline"
          ? "outside"
          : props.variant === "horizontal"
          ? "outside-left"
          : undefined
      }
      title={typeof props.title === "string" ? props.title : undefined}
    >
      {(item: object) => {
        const typedItem = item as T;
        return (
          <NextUISelectItem
            key={typedItem.id}
            classNames={{
              base: clsx(
                "!text-gray-900",
                "rounded-lg !bg-white p-2 !outline-none ring-1 ring-transparent",
                "data-[focus=true]:!bg-primary-light data-[focus=true]:ring-primary",
                "data-[hover=true]:!bg-primary-light data-[hover=true]:ring-primary",
                "data-[selected=true]:!bg-primary-light data-[selected=true]:font-semibold data-[selected=true]:ring-primary",
              ),
            }}
          >
            {renderItem(typedItem)}
          </NextUISelectItem>
        );
      }}
    </ExtendedSelect>
  );
}

Select.displayName = "RecareUI.Select";
